/**
 * Return Prime OpenAPI v1 Handler
 * Encapsulates all OpenAPI v1 Shopper & Admin endpoints with full payload structures.
 */
window.ReturnPrimeAPI = (function() {
  const CONFIG = {
    baseURL: 'https://rp-api-gw.returnprime.co/public-api',
    storeDomain: (window.Shopify && window.Shopify.shop) ? window.Shopify.shop : 'bluer-co-in.myshopify.com',
    rpToken: '3db7b78b0e988dd63558eaf608e09ffa408000254692133d5be70c0f17e82225'
  };

  /**
   * Base Fetch Wrapper
   */
  async function request(endpoint, method = 'GET', body = null) {
    const url = `${CONFIG.baseURL}${endpoint}`;
    const headers = {
      'X-RP-TOKEN': CONFIG.rpToken,
      'X-SHOP-DOMAIN': CONFIG.storeDomain,
      'Content-Type': 'application/json'
    };

    const options = {
      method: method,
      headers: headers
    };

    if (body && (method === 'POST' || method === 'PUT')) {
      options.body = JSON.stringify(body);
    }

    try {
      const response = await fetch(url, options);
      const data = await response.json();

      if (!response.ok || !data.status) {
        const rawCode = data.messageCode || '';
        const rawMsg = String(data.message || '');
        let cleanMsg = 'Unable to process request. Please try again.';

        if (rawCode === 'CUSTOMER_E5' || rawMsg.includes('no_fulfilled_order_found')) {
          cleanMsg = 'No fulfilled order found. Please check your Order ID and Email / Phone Number.';
        } else if (rawCode === 'SESSION_EXPIRED' || rawMsg.includes('session_expired')) {
          cleanMsg = 'Your session has expired. Please search for your order again.';
        } else if (rawCode === 'NOT_FOUND' || rawMsg.includes('not_found')) {
          cleanMsg = 'Order not found. Please verify your Order ID and Email Address.';
        } else if (rawCode === 'CONFLICT' || rawMsg.includes('conflict')) {
          cleanMsg = 'A return request has already been submitted for this item or order.';
        } else if (rawCode === 'BAD_REQUEST') {
          cleanMsg = 'Invalid request details. Please check your inputs and try again.';
        } else if (rawMsg && !rawMsg.startsWith('Request failed')) {
          cleanMsg = rawMsg.replace(/^[a-z0-9_]+\./i, '').replace(/_/g, ' ');
          cleanMsg = cleanMsg.charAt(0).toUpperCase() + cleanMsg.slice(1);
        }

        throw new Error(cleanMsg);
      }
      return data;
    } catch (err) {
      console.error(`[ReturnPrimeAPI Error] ${method} ${endpoint}:`, err);
      throw err;
    }
  }

  return {
    config: CONFIG,

    // =========================================================================
    // PART A: Customer Shopper Request Flow (OpenAPI v1)
    // =========================================================================

    /**
     * A1. Create Session
     * POST /v1/sessions
     * Bootstraps a short-lived customer session bound to an order + identifier.
     */
    async createSession(orderName, identifier) {
      const cleanOrder = String(orderName).trim().replace(/^#/, '');
      const cleanIdentifier = String(identifier).trim();

      const response = await request('/v1/sessions', 'POST', {
        order: cleanOrder,
        store: CONFIG.storeDomain,
        identifier: cleanIdentifier
      });

      // Returns sessionId string
      return response.data;
    },

    /**
     * A2. Get Session Metadata
     * GET /v1/sessions/{sessionId}
     */
    async getSession(sessionId) {
      const response = await request(`/v1/sessions/${encodeURIComponent(sessionId)}`, 'GET');
      return response.data;
    },

    /**
     * Get Session Data
     * GET /v1/session-data?session={sessionId}
     */
    async getSessionData(sessionId) {
      try {
        const response = await request(`/v1/session-data?session=${encodeURIComponent(sessionId)}`, 'GET');
        return response.data;
      } catch (_) {
        const response = await request(`/v1/session-data/${encodeURIComponent(sessionId)}`, 'GET');
        return response.data;
      }
    },

    /**
     * A3. Find Order Details & Line Items
     * GET /v1/orders/find?session={sessionId}
     * Returns full order, line items, returnability flags, and existing requests.
     */
    async findOrder(sessionId) {
      const response = await request(`/v1/orders/find?session=${encodeURIComponent(sessionId)}`, 'GET');
      return response.data;
    },

    /**
     * List all return/exchange requests for a session
     * GET /v1/requests?session={sessionId}
     */
    async listRequests(sessionId, options = {}) {
      let queryStr = `session=${encodeURIComponent(sessionId)}`;
      if (typeof options === 'string') {
        queryStr += `&request_number=${encodeURIComponent(options)}`;
      } else if (options && typeof options === 'object') {
        if (options.request_number) queryStr += `&request_number=${encodeURIComponent(options.request_number)}`;
        if (options.request_id) queryStr += `&request_id=${encodeURIComponent(options.request_id)}`;
        if (options.order_name) queryStr += `&order_name=${encodeURIComponent(options.order_name)}`;
      }
      const response = await request(`/v1/requests?${queryStr}`, 'GET');
      return response.data;
    },

    /**
     * A4. Get Advantage Flag
     * GET /v1/advantage/check?session={sessionId}
     */
    async getAdvantageFlag(sessionId) {
      const response = await request(`/v1/advantage/check?session=${encodeURIComponent(sessionId)}`, 'GET');
      return response.data;
    },

    /**
     * A5. Get Incentive
     * GET /v1/incentive?session={sessionId}
     */
    async getIncentive(sessionId) {
      const response = await request(`/v1/incentive?session=${encodeURIComponent(sessionId)}`, 'GET');
      return response.data;
    },

    /**
     * A6. List Reasons
     * POST /v1/reasons
     * Returns applicable return/exchange reasons for selected line items.
     */
    async listReasons(sessionId, orderId, lineItemIds, type = 'return') {
      const response = await request('/v1/reasons', 'POST', {
        session: sessionId,
        order: orderId,
        line_items: lineItemIds,
        type: type
      });
      return response.data;
    },

    /**
     * A7. Get Reason Refund Modes
     * GET /v1/reasons/{reasonId}/refund-modes?session={sessionId}
     */
    async getRefundModes(reasonId, sessionId) {
      const response = await request(`/v1/reasons/${encodeURIComponent(reasonId)}/refund-modes?session=${encodeURIComponent(sessionId)}`, 'GET');
      return response.data;
    },

    /**
     * A8. Get Reason Return Methods
     * GET /v1/reasons/{reasonId}/return-methods?session={sessionId}
     */
    async getReturnMethods(reasonId, sessionId) {
      const response = await request(`/v1/reasons/${encodeURIComponent(reasonId)}/return-methods?session=${encodeURIComponent(sessionId)}`, 'GET');
      return response.data;
    },

    /**
     * A9. List Upsell Suggestions (Exchange)
     * POST /v1/products/upsell
     */
    async listUpsellProducts(sessionId, originalProductIds, page = 1, limit = 10) {
      const response = await request('/v1/products/upsell', 'POST', {
        session: sessionId,
        original_product_ids: originalProductIds,
        pagination: { page, limit }
      });
      return response.data;
    },

    /**
     * A10. Get Product Details (Exchange)
     * GET /v1/products/{productId}?session={sessionId}
     */
    async getProductDetails(productId, sessionId) {
      const response = await request(`/v1/products/${encodeURIComponent(productId)}?session=${encodeURIComponent(sessionId)}`, 'GET');
      return response.data;
    },

    /**
     * A11. Browse Catalog (Exchange)
     * GET /v1/products?session={sessionId}&page=1&limit=20
     */
    async browseCatalog(sessionId, options = {}) {
      const params = new URLSearchParams({ session: sessionId, page: 1, limit: 20, ...options });
      const response = await request(`/v1/products?${params.toString()}`, 'GET');
      return response.data;
    },

    /**
     * A13. Calculate Fee
     * POST /v1/fees
     */
    async calculateFee(sessionId, orderId, lineItemsPayload) {
      const response = await request('/v1/fees', 'POST', {
        session: sessionId,
        order: orderId,
        line_items: lineItemsPayload
      });
      return response.data;
    },

    /**
     * A18. Create Return Request (No Payment)
     * POST /v1/requests
     */
    async createReturnRequest(sessionId, orderId, lineItemsPayload, addressObj = {}) {
      const response = await request('/v1/requests', 'POST', {
        session: sessionId,
        order: orderId,
        payment: null,
        order_manual_payment: false,
        gift_return: false,
        green_return: false,
        promotion: false,
        isReRequested: false,
        reRequestId: null,
        client_details: {
          accept_language: ['en-US', 'en'],
          user_agent: navigator.userAgent,
          proxy: true
        },
        line_items: lineItemsPayload,
        address: addressObj
      });
      return response.data;
    },

    /**
     * E1. File Upload
     * POST /v1/files/upload
     * Uploads images/videos via multipart/form-data to Return Prime CDN.
     */
    async uploadFiles(fileList, sessionId) {
      if (!fileList || fileList.length === 0) return [];
      const formData = new FormData();

      if (sessionId) {
        formData.append('session', sessionId);
      }
      formData.append('relativePath', 'null');

      Array.from(fileList).forEach(file => {
        formData.append('name', file.name);
        formData.append('type', file.type);
        formData.append('files', file);
      });

      const url = `${CONFIG.baseURL}/v1/files/upload${sessionId ? `?session=${encodeURIComponent(sessionId)}` : ''}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'X-RP-TOKEN': CONFIG.rpToken,
          'X-SHOP-DOMAIN': CONFIG.storeDomain
        },
        body: formData
      });
      const data = await response.json();
      if (!response.ok || !data.status) {
        throw new Error(data.message || 'File upload failed');
      }
      return data.data || [];
    },

    /**
     * A14. Check Promotion
     * POST /v1/promotions/check
     */
    async checkPromotions(sessionId, orderId, reasonsArray, lineItemsArray, refundModesArray = ['store_credit', 'default']) {
      const response = await request('/v1/promotions/check', 'POST', {
        session: sessionId,
        order: Number(orderId),
        reasons: Array.isArray(reasonsArray) ? reasonsArray : [reasonsArray],
        refund_modes: refundModesArray,
        line_items: lineItemsArray.map(item => ({
          id: Number(item.id || item.line_item_id),
          quantity: Number(item.quantity || 1)
        })),
        order_manual_payment: true
      });
      return response.data;
    },

    /**
     * A15. Get Request Details
     * GET /v1/requests/{requestId}?session={sessionId}
     */
    async getRequestDetails(requestId, sessionId) {
      const response = await request(`/v1/requests/${encodeURIComponent(requestId)}?session=${encodeURIComponent(sessionId)}`, 'GET');
      return response.data;
    }
  };
})();
